import { css, CSSResult } from 'lit'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { csBorder, csTransition } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { csColorBackground, csColorBorder, csColorFunctional } from '@cloudsuite/mosaic-shared/styles/colors.style.js'


export const cscLightboxThumbnails = {
  gap: css`var(--csc-lightbox-thumbnail-gap, ${csSpacing.spacing05})`,
  minWidth: css`var(--csc-lightbox-thumbnail-min-width, var(--csc-lightbox-thumbnail-min-size, 60px))`,
  maxWidth: css`var(--csc-lightbox-thumbnail-max-width, var(--csc-lightbox-thumbnail-max-size, 90px))`,
  minHeight: css`var(--csc-lightbox-thumbnail-min-height, var(--csc-lightbox-thumbnail-min-width, var(--csc-lightbox-thumbnail-min-size, 60px)))`,
  maxHeight: css`var(--csc-lightbox-thumbnail-max-height, var(--csc-lightbox-thumbnail-max-size, 90px))`,
  activeBorderColor: css`var(--csc-lightbox-thumbnail-active-border-color, ${csColorFunctional.info})`,
  borderColor: css`var(--csc-lightbox-thumbnail-border-color, ${csColorBorder.light})`,
  borderRadius: css`var(--csc-lightbox-thumbnail-border-radius, ${csBorder.radiusMinimal})`,
  fadeWidth: css`var(--csc-lightbox-thumbnail-fade-width, 50px)`,
  fadeHeight: css`var(--csc-lightbox-thumbnail-fade-height, 90px)`,
  verticalFadeHeight: css`var(--csc-lightbox-thumbnail-vertical-fade-height, var(--csc-lightbox-thumbnail-fade-width, 50px))`,
  placeholderBackgroundColor: css`var(--csc-lightbox-thumbnail-placeholder-background-color, ${csColorBackground.secondary})`,
  backgroundColor: css`var(--csc-lightbox-thumbnail-background-color, transparent)`,
  padding: css`var(--csc-lightbox-thumbnail-padding, 0)`,
  borderWidth: css`var(--csc-lightbox-thumbnail-border-width, ${csBorder.width})`,
  hoverBorderColor: css`var(--csc-lightbox-thumbnail-hover-border-color, ${csColorBorder.dark})`,
}

const fluidSize = css`clamp(${cscLightboxThumbnails.minWidth}, 22.5cqw, ${cscLightboxThumbnails.maxWidth})`

export const style: CSSResult = css`
  :host {
    display: block;
  }

  :host([alignment='stretch']:not([orientation='vertical'])) {
    width: 100%;
  }

  :host([alignment='stretch'][orientation='vertical']) {
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
  }

  :host([alignment='center']) {
    width: 100%;
  }

  :host([hidden]) {
    display: none;
  }

  .thumbnails-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .thumbnails-wrapper.alignment-stretch {
    width: 100%;
  }

  .thumbnails-wrapper.alignment-stretch.orientation-vertical {
    width: 100%;
    height: 100%;
  }

  .thumbnails-wrapper.alignment-center {
    width: 100%;
    justify-content: center;
  }

  .thumbnails-wrapper.alignment-center .thumbnails-viewport {
    flex: 0 1 auto;
    max-width: 100%;
  }

  .thumbnails-wrapper.alignment-center .csc-lightbox-thumbnails {
    justify-content: safe center;
  }

  .thumbnails-wrapper.orientation-vertical {
    flex-direction: column;
    height: 100%;
    align-items: stretch;
  }

  .thumbnails-viewport {
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .thumbnails-wrapper:not(.orientation-vertical) .thumbnails-viewport {
    width: 100%;
    min-height: ${cscLightboxThumbnails.minHeight};
  }

  .thumbnails-wrapper.orientation-vertical .thumbnails-viewport {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .csc-lightbox-thumbnails {
    container-type: inline-size;
    display: flex;
    align-items: center;
    gap: ${cscLightboxThumbnails.gap};
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    width: 100%;
    min-width: 0;
 
  }

  .csc-lightbox-thumbnails::-webkit-scrollbar {
    display: none;
  }

  .csc-lightbox-thumbnails.orientation-vertical {
    container-type: block-size;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    width: 100%;
    height: 100%;
  }

  .thumbnail {
    position: relative;
    flex: 0 0 ${fluidSize};
    width: ${fluidSize};
    height: ${fluidSize};
    overflow: hidden;
    cursor: pointer;
    transition: transform ${csTransition.duration} ${csTransition.timingFunction}, zoom-in;
    box-sizing: border-box;
    border-color: ${cscLightboxThumbnails.borderColor};
    border-radius: ${cscLightboxThumbnails.borderRadius};
    border-style: solid;
    border-width: ${cscLightboxThumbnails.borderWidth};
    padding: ${cscLightboxThumbnails.padding};
    background: ${cscLightboxThumbnails.backgroundColor};
  }

  .csc-lightbox-thumbnails.orientation-vertical .thumbnail {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    max-width: ${cscLightboxThumbnails.maxWidth};
    max-height: ${cscLightboxThumbnails.maxHeight};
  }

  .thumbnail:hover {
    border-color: ${cscLightboxThumbnails.hoverBorderColor};
  }

  .thumbnail.active {
    border-color: ${cscLightboxThumbnails.activeBorderColor};
  }

  .thumbnail cse-image,
  .thumbnail .video-placeholder {
    width: 100%;
    height: 100%;
    display: block;
  }

  .thumbnail cse-image {
    --cse-image-aspect-ratio: 1 / 1;
  }

  .thumbnail .video-placeholder {
    background: ${cscLightboxThumbnails.placeholderBackgroundColor};
  }

  .thumbnail .video-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    pointer-events: none;
    color: white;
  }

  .scroll-fade-left,
  .scroll-fade-right,
  .scroll-fade-top,
  .scroll-fade-bottom {
    pointer-events: none;
    z-index: 2;
  }

  .scroll-fade-left {
    position: absolute;
    top: 50%;
    left: 0;
    width: ${cscLightboxThumbnails.fadeWidth};
    height: ${cscLightboxThumbnails.fadeHeight};
    transform: translateY(-50%);
    background: linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  }

  .scroll-fade-right {
    position: absolute;
    top: 50%;
    right: 0;
    width: ${cscLightboxThumbnails.fadeWidth};
    height: ${cscLightboxThumbnails.fadeHeight};
    transform: translateY(-50%);
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }

  .scroll-fade-top {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${cscLightboxThumbnails.verticalFadeHeight};
    background: linear-gradient(180deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  }

  .scroll-fade-bottom {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: ${cscLightboxThumbnails.verticalFadeHeight};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }

  .thumbnails-wrapper.alignment-stretch .csc-lightbox-thumbnails.orientation-horizontal {
    overflow-x: auto;
  }

  .thumbnails-wrapper.alignment-stretch .thumbnails-viewport {
    width: 100%;
  }

  .thumbnails-wrapper.alignment-stretch .csc-lightbox-thumbnails.orientation-vertical {
    overflow-y: auto;
    height: 100%;
    width: 100%;
    flex: 1;
    min-height: 0;
  }
`
